import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './RoundedSelect.css';

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path
      d="M2 4L6 8L10 4"
      stroke="#333"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RoundedSelect = ({
  label,
  placeholder = 'Select...',
  value,
  options = [],
  onChange,
  className = '',
  disabled = false,
  overlayDropdown = false,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [overlayRect, setOverlayRect] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const labelId = useId();
  const listboxId = useId();

  const selectedOption = options.find(option => option.value === value);
  const displayLabel = value ? selectedOption?.label : null;

  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const updateOverlayPosition = useCallback(() => {
    if (!overlayDropdown || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setOverlayRect({
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    });
  }, [overlayDropdown]);

  const toggleOpen = () => {
    if (disabled) return;
    setOpen(current => {
      const next = !current;
      if (next) {
        const selectedIndex = options.findIndex(option => option.value === value);
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
      } else {
        setActiveIndex(-1);
      }
      return next;
    });
  };

  const selectOption = option => {
    if (disabled) return;
    onChange?.(option.value);
    close();
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = event => {
      const target = event.target;
      if (rootRef.current?.contains(target) || listRef.current?.contains(target)) {
        return;
      }
      close();
    };

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !overlayDropdown) return undefined;

    updateOverlayPosition();
    const handleReposition = () => updateOverlayPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, overlayDropdown, updateOverlayPosition]);

  const handleTriggerKeyDown = event => {
    if (disabled) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        const selectedIndex = options.findIndex(option => option.value === value);
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        setOpen(true);
        return;
      }

      setActiveIndex(current => {
        if (!options.length) return -1;
        if (event.key === 'ArrowDown') {
          return current < options.length - 1 ? current + 1 : 0;
        }
        return current > 0 ? current - 1 : options.length - 1;
      });
    }

    if (open && (event.key === 'Enter' || event.key === ' ')) {
      if (activeIndex >= 0 && options[activeIndex]) {
        event.preventDefault();
        selectOption(options[activeIndex]);
      }
    }

    if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    }
  };

  const panel = open ? (
    <div
      ref={listRef}
      className={`rounded-select-panel${
        overlayDropdown ? ' rounded-select-panel--overlay' : ''
      }`}
      style={
        overlayDropdown && overlayRect
          ? {
              top: overlayRect.top,
              left: overlayRect.left,
              width: overlayRect.width,
            }
          : undefined
      }
    >
      <ul
        id={listboxId}
        className="rounded-select-options"
        role="listbox"
        aria-labelledby={label ? labelId : undefined}
      >
        {options.map((option, index) => {
          const selected = option.value === value;
          return (
            <li key={option.value} role="presentation">
              <button
                id={`${listboxId}-option-${index}`}
                type="button"
                role="option"
                aria-selected={selected}
                className={`rounded-select-option${
                  selected || index === activeIndex ? ' is-active' : ''
                }${option.secondary ? ' is-secondary' : ''}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectOption(option)}
              >
                {option.label}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  return (
    <div
      ref={rootRef}
      className={`rounded-select${open ? ' is-open' : ''}${
        disabled ? ' is-disabled' : ''
      } ${className}`.trim()}
    >
      {label ? (
        <span id={labelId} className="rounded-select-label">
          {label}
        </span>
      ) : null}
      <button
        ref={triggerRef}
        type="button"
        className="rounded-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={label ? labelId : undefined}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={
          open && activeIndex >= 0
            ? `${listboxId}-option-${activeIndex}`
            : undefined
        }
        disabled={disabled}
        onClick={toggleOpen}
        onKeyDown={handleTriggerKeyDown}
      >
        <span className={displayLabel ? 'rounded-select-value' : 'rounded-select-placeholder'}>
          {displayLabel || placeholder}
        </span>
        <span className={`rounded-select-chevron${open ? ' is-open' : ''}`}>
          <ChevronIcon />
        </span>
      </button>
      {overlayDropdown && typeof document !== 'undefined'
        ? createPortal(panel, document.body)
        : panel}
    </div>
  );
};

export default RoundedSelect;
